/**
 * ThinkingPanel component for displaying LLM thinking and tool execution steps.
 *
 * Shows a collapsible panel with the LLM's reasoning and tool calls,
 * styled to match the portfolio design with gradient background and left border.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { SparkFlow } from '../../types/sparkTypes';
import { ThinkingStep } from '../../types/thinkingTypes';
import styles from './ThinkingPanel.module.css';
import { normalizeMarkdownBullets } from '../../utils/markdown';
import { getActionDisplayName } from '../../utils/actionPresentation';

interface ThinkingPanelProps {
  /** List of thinking steps to display */
  steps: ThinkingStep[];
  /** Whether the panel is expanded */
  isExpanded: boolean;
  /** Callback when expand/collapse is toggled */
  onToggle: () => void;
  /** Optional Spark flow for action-aware tool labels */
  sparkFlow?: SparkFlow | null;
}

/**
 * Formats a tool name for display (e.g., "read_briefing" -> "Read briefing")
 */
function formatToolName(toolName: string, sparkFlow: SparkFlow | null): string {
  if (toolName.startsWith('trigger_')) {
    return getActionDisplayName(toolName.replace(/^trigger_/, ''), sparkFlow);
  }

  if (sparkFlow?.actions?.some((action) => action.name === toolName)) {
    return getActionDisplayName(toolName, sparkFlow);
  }

  return toolName
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}

/**
 * Formats tool arguments for display
 */
function formatToolArgs(args: Record<string, unknown> | undefined): string {
  if (!args || Object.keys(args).length === 0) {
    return '';
  }

  // Show a brief summary of args
  const entries = Object.entries(args).slice(0, 2);
  const summary = entries
    .map(([key, value]) => {
      const valueStr = typeof value === 'string'
        ? value.length > 30 ? `${value.slice(0, 30)}...` : value
        : JSON.stringify(value).slice(0, 30);
      return `${key}: ${valueStr}`;
    })
    .join(', ');

  return entries.length < Object.keys(args).length
    ? `${summary}, ...`
    : summary;
}

/**
 * Checks if a tool result indicates an error
 */
function isErrorResult(result: string | undefined): boolean {
  if (!result) return false;
  return result.startsWith('ERROR:') || result.startsWith('Error:');
}

export function ThinkingPanel({
  steps,
  isExpanded,
  onToggle,
  sparkFlow = null,
}: ThinkingPanelProps): React.ReactElement | null {
  if (steps.length === 0) {
    return null;
  }

  const isProcessing = steps.some((s) => s.status === 'pending');

  return (
    <div className={styles.panel}>
      <button
        className={styles.header}
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls="thinking-steps-content"
      >
        <span className={styles.headerTitle}>
          {isProcessing && <span className={styles.spinner} aria-label="Processing" />}
          Thoughts
          <span className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}>
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 5.646a.5.5 0 01.708 0L8 8.293l2.646-2.647a.5.5 0 01.708.708l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.708z" />
            </svg>
          </span>
        </span>
      </button>

      {isExpanded && (
        <div id="thinking-steps-content" className={styles.content}>
          {steps.map((step) => (
            <div key={step.id} className={styles.step}>
              {step.type === 'thinking' ? (
                // Thinking content with left border accent
                <div className={styles.thinkingBlock}>
                  {step.thinkingContent ? (
                    <div className={styles.thinkingContent}>
                      <ReactMarkdown>{normalizeMarkdownBullets(step.thinkingContent)}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className={styles.thinkingPlaceholder}>
                      Analyzing request...
                    </div>
                  )}
                </div>
              ) : (
                // Tool call with status indicator
                <div className={styles.toolCall}>
                  <span className={styles.toolIcon}>
                    {step.status === 'pending' ? (
                      <span className={styles.toolSpinner} />
                    ) : isErrorResult(step.result) ? (
                      <span className={styles.toolError}>
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z" />
                        </svg>
                      </span>
                    ) : (
                      <span className={styles.toolCheck}>
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                        </svg>
                      </span>
                    )}
                  </span>
                  <div className={styles.toolContent}>
                    <span className={styles.toolName}>
                      {step.toolName
                        ? formatToolName(step.toolName, sparkFlow)
                        : formatToolName('Tool call', sparkFlow)}
                    </span>
                    {step.toolArgs && (
                      <span className={styles.toolArgs}>
                        {formatToolArgs(step.toolArgs)}
                      </span>
                    )}
                    {step.status === 'completed' && step.result && (
                      <span className={isErrorResult(step.result) ? styles.toolResultError : styles.toolResult}>
                        {step.result.length > 100
                          ? `${step.result.slice(0, 100)}...`
                          : step.result}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
